import { Account } from '@packages/models/api';
import amplitude from 'amplitude-js';
import { ConfigService, Constants } from '../config';
import { Platform } from 'react-native';
import { v4 as uuid } from 'uuid';

import {
    ANALYTICS_EVENTS,
    ANALYTICS_ROLE_ID_MAP,
    ANALYTICS_ROLE_SYNONYMS,
    ANALYTICS_UNKNOWN_ROLE,
    ANALYTICS_USER_ROLES,
    AnalyticsEventData,
} from './amplitude/amplitude-event-types';
import { convertToSnakeCase, maskEventConfig, maskPii } from './amplitude/masking-utils';
import { getLogger } from '../logging';

const logger = getLogger('amplitude');

type AnalyticsEventDataEnricher = (
    event: ANALYTICS_EVENTS,
    data?: AnalyticsEventData
) => AnalyticsEventData | undefined;

/**
 * This service is currently solely responsible for logging events to Amplitude.
 */
export class AnalyticsService {
    private configService: ConfigService;
    private analyticsInstance: amplitude.AmplitudeClient;
    private user: Account | undefined;
    private userRole: string = ANALYTICS_UNKNOWN_ROLE;

    /**
     * this is the journey id, will be set when user is starting the inreaction with
     * the system. most prefeabaly when user is getting set.
     * this is simple uuid that uniquely identifies the journey.
     * this is a mandatory property in event logging.
     */
    private journey_id: string;
    private correlationId: string | undefined;
    private correlationFlow: string | undefined;
    private correlationStartAt: Date | undefined;
    private eventDataEnricher?: AnalyticsEventDataEnricher;
    private entry_point?: string;
    private report_type?: string;
    private sts_id?: string;
    private form_completion_percent?: number;
    private fail_code_count?: number;
    private fail_code_category?: string;

    constructor(configService: ConfigService) {
        this.configService = configService;
        this.analyticsInstance = amplitude.getInstance();
        this.journey_id = uuid();
        try {
            this.analyticsInstance.init(configService.get(Constants.Env.amplitudeApiKey) || '', '', {
                platform: Platform.OS,
            });
            this.analyticsInstance.setSessionId(new Date().getTime());
        } catch (e) {
            logger.warn('Amplitude initialization failed', { error: e });
        }
    }

    public setUser(user: Account) {
        this.user = user;
        this.userRole = this.resolveUserRole(user);
        const maskedUsername = maskPii(this.user?.soaUsername);
        try {
            this.analyticsInstance.setUserId(maskedUsername);
            //reset the session id
            this.analyticsInstance.setSessionId(new Date().getTime());
            this.analyticsInstance.setUserProperties({
                username: maskedUsername,
                userRole: this.userRole,
                userBaseRoles: this.user?.baseRolesDescription ? this.user.baseRolesDescription : 'N/A',
                userAddOnRoles: this.user?.addOnRolesDescription ? this.user.addOnRolesDescription : 'N/A',
                locations: this.user?.locationsDescription ? this.user.locationsDescription : 'N/A',
                user_role: this.userRole,
            });
        } catch (e) {
            logger.warn('Failed to set the analytics user', { error: e });
        }
    }

    public ensureUser(user: Account) {
        const isSameUser = this.user && this.user.soaUsername === user.soaUsername;
        if (!isSameUser || this.userRole === ANALYTICS_UNKNOWN_ROLE) {
            this.setUser(user);
        }
    }

    public setEventDataEnricher(enricher?: AnalyticsEventDataEnricher) {
        this.eventDataEnricher = enricher;
    }

    public logEvent(event: ANALYTICS_EVENTS, data?: AnalyticsEventData, callback?: () => void, flow?: string) {
        // Handle correlation flow
        if (flow !== undefined) {
            if (flow === 'stop') {
                // End the current correlation
                const sessionDurationMin = this.correlationStartAt
                    ? Math.round((new Date().getTime() - this.correlationStartAt.getTime()) / 60000)
                    : undefined;
                data = { ...data, ...(sessionDurationMin !== undefined && { sessionDuration: sessionDurationMin }) };
                this.correlationId = undefined;
                this.correlationFlow = undefined;
                this.correlationStartAt = undefined;
            } else if (flow.length > 0) {
                // Start a new correlation
                if (this.correlationId && this.correlationStartAt) {
                    // Previous correlation will be implicitly ended when new one starts
                }
                // Start new correlation
                this.correlationId = uuid();
                this.correlationFlow = flow;
                this.correlationStartAt = new Date();
            }
        }
        const enrichedData = this.getEnrichedEventData(event, data);

        const eventConfig = {
            timeStamp: new Date().toString(),
            environment: this.configService.currentEnvName,
            journey_id: this.journey_id,
            correlationId: this.correlationId,
            correlationFlow: this.correlationFlow,
            correlationStartAt: this.correlationStartAt?.toISOString(),
            module_name: this.resolveModuleName(enrichedData),
            platform: Platform.OS,
            user_role: this.resolveEventUserRole(enrichedData),
            entry_point: this.entry_point,
            report_type: this.report_type,
            sts_id: this.sts_id,
            form_completion_percent: this.form_completion_percent,
            fail_code_count: this.fail_code_count,
            fail_code_category: this.fail_code_category,
            result_status: 'N/A',
            error_code: 'N/A',
            ...enrichedData,
        };
        const maskedEventConfig = convertToSnakeCase(maskEventConfig(eventConfig as Record<string, unknown>));
        try {
            this.analyticsInstance.logEvent(event, maskedEventConfig, () => {
                logger.debug(`Analytics event fired: [${event}]`, {
                    config: maskedEventConfig,
                    event,
                });
                if (callback) {
                    callback();
                }
            });
        } catch (e) {
            logger.warn(`Failed to log the analytics event [${event}]`, { error: e, event });
            if (callback) callback();
        }
    }

    private getEnrichedEventData(event: ANALYTICS_EVENTS, data?: AnalyticsEventData): AnalyticsEventData | undefined {
        if (!this.eventDataEnricher) {
            return data;
        }

        try {
            return this.eventDataEnricher(event, data);
        } catch (e) {
            logger.warn(`The analytics event data enricher failed for [${event}]`, { error: e, event });
            return data;
        }
    }

    private resolveEventUserRole(data?: AnalyticsEventData): string {
        const providedRole = data?.user_role;
        if (typeof providedRole === 'string') {
            const normalizedRole = this.normalizeRole(providedRole) || providedRole;
            if (normalizedRole && normalizedRole !== ANALYTICS_UNKNOWN_ROLE) {
                return normalizedRole;
            }
        }

        if (this.userRole && this.userRole !== ANALYTICS_UNKNOWN_ROLE) {
            return this.userRole;
        }

        if (this.user) {
            const resolvedFromUser = this.resolveUserRole(this.user);
            this.userRole = resolvedFromUser;
            return resolvedFromUser;
        }

        return ANALYTICS_UNKNOWN_ROLE;
    }

    private resolveUserRole(account?: Account): string {
        if (!account) {
            return ANALYTICS_UNKNOWN_ROLE;
        }

        const candidateRoles: string[] = [];

        if (account.addOnRolesDescription) {
            candidateRoles.push(...account.addOnRolesDescription.split(',').map((role) => role.trim()));
        }

        if (account.profiles && account.profiles.length) {
            account.profiles.forEach((profile) => {
                if (profile.roleDescription) {
                    candidateRoles.push(profile.roleDescription);
                }
                if (profile.roleId) {
                    candidateRoles.push(profile.roleId);
                }
            });
        }

        if (account.baseRoleIds && account.baseRoleIds.length) {
            candidateRoles.push(...account.baseRoleIds);
        }

        if (account.baseRoles && account.baseRoles.length) {
            account.baseRoles.forEach((baseRole) => {
                if ((baseRole as any).description) {
                    candidateRoles.push((baseRole as any).description);
                }
                if (baseRole.roleDescription) {
                    candidateRoles.push(baseRole.roleDescription);
                }
                if (baseRole.defaultRoleDescription) {
                    candidateRoles.push(baseRole.defaultRoleDescription);
                }
                if (baseRole.roleId) {
                    candidateRoles.push(baseRole.roleId);
                }
            });
        }

        if (account.addOnRoles && account.addOnRoles.length) {
            account.addOnRoles.forEach((role) => {
                if (role.description) {
                    candidateRoles.push(role.description);
                }
                if (role.roleId) {
                    candidateRoles.push(role.roleId);
                }
            });
        }

        for (const rawRole of candidateRoles) {
            const normalizedRole = this.normalizeRole(rawRole);
            if (normalizedRole) {
                return normalizedRole;
            }
        }

        return ANALYTICS_UNKNOWN_ROLE;
    }
    private resolveModuleName(enrichedData?: any): string {
        const pathName = typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : '/';
        // Because this is outside of the React Router context, we cannot use the `useResolvedPath` hook to get the resolved path.
        // Instead, we will use the `window.location.pathname` to get the current path and then resolve the module name based on that.
        const isPublicLandingPage = pathName === '/' && enrichedData?.state !== 'AUTHENTICATED';
        const declaredModule = pathName.slice(1, pathName.length).split('/')[0];
        // Can't use ModuleName enums here, so had to hardcode string
        const moduleName = isPublicLandingPage ? 'public-landing' : declaredModule;
        return moduleName;
    }
    private normalizeRole(rawRole?: string): string | undefined {
        if (!rawRole) {
            return undefined;
        }

        const trimmed = rawRole.trim();
        if (!trimmed) {
            return undefined;
        }

        if (ANALYTICS_USER_ROLES.has(trimmed)) {
            return trimmed;
        }

        const lower = trimmed.toLowerCase();

        for (const knownRole of Array.from(ANALYTICS_USER_ROLES)) {
            if (knownRole.toLowerCase() === lower) {
                return knownRole;
            }
        }

        const normalizedIdKey = trimmed.replace(/-/g, '_').toUpperCase();
        if (ANALYTICS_ROLE_ID_MAP[normalizedIdKey as keyof typeof ANALYTICS_ROLE_ID_MAP]) {
            return ANALYTICS_ROLE_ID_MAP[normalizedIdKey as keyof typeof ANALYTICS_ROLE_ID_MAP];
        }

        if (ANALYTICS_ROLE_SYNONYMS[lower]) {
            return ANALYTICS_ROLE_SYNONYMS[lower];
        }

        return undefined;
    }
}
